import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import type { AspiranteRow, CrearAspiranteResponse } from './types.js';

// ── Helpers de formato ────────────────────────────────────────────────────────

const EVAL_BASE = import.meta.env['VITE_EVAL_URL'] ?? 'https://evaluacion.leadsphere.com';

function fmtFecha(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

type InvEstado = 'generada' | 'iniciada' | 'en_progreso' | 'completada' | 'expirada';

function invBadge(estado: InvEstado) {
  const map: Record<InvEstado, { cls: string; label: string }> = {
    generada:    { cls: 'badge-neutral',  label: 'Generada' },
    iniciada:    { cls: 'badge-info',     label: 'Iniciada' },
    en_progreso: { cls: 'badge-warning',  label: 'En progreso' },
    completada:  { cls: 'badge-success',  label: 'Completada' },
    expirada:    { cls: 'badge-danger',   label: 'Expirada' },
  };
  const { cls, label } = map[estado] ?? { cls: 'badge-neutral', label: estado };
  return <span className={`badge ${cls}`}>{label}</span>;
}

function aplicacionBadge(estado: string) {
  const map: Record<string, { cls: string; label: string }> = {
    pendiente:  { cls: 'badge-neutral',  label: 'Pendiente' },
    iniciada:   { cls: 'badge-info',     label: 'Iniciada' },
    enviada:    { cls: 'badge-warning',  label: 'Enviada' },
    calificada: { cls: 'badge-success',  label: 'Calificada' },
  };
  const { cls, label } = map[estado] ?? { cls: 'badge-neutral', label: estado };
  return <span className={`badge ${cls}`}>{label}</span>;
}

// Mismo orden que los encabezados de la tabla.
const EXAMENES_COLUMNAS = ['cleaver', 'kostick', 'terman', 'lifo'] as const;

// ── Modal: Nueva evaluación ───────────────────────────────────────────────────

interface NuevaEvalModalProps {
  onClose: () => void;
  onSuccess: (res: CrearAspiranteResponse) => void;
}

function NuevaEvaluacionModal({ onClose, onSuccess }: NuevaEvalModalProps) {
  const { post } = useApi();
  const [form, setForm] = useState({
    nombre_completo: '',
    email: '',
    puesto_aplicado: '',
    vigencia_horas: 120,
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      post<CrearAspiranteResponse>('/psicometricos/aspirantes', {
        ...form,
        // BH-4C — toda invitación nueva incluye los 4 exámenes siempre, sin
        // selector en la UI. kostick/terman no tienen formulario todavía —
        // apps/aspirantes los muestra como "Próximamente" hasta que existan.
        examenes_asignados: ['cleaver', 'kostick', 'terman', 'lifo'],
      }),
    onSuccess: (res) => {
      onSuccess(res);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'No fue posible crear al aspirante');
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <div className="backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-hdr">
          <span className="modal-title">Nueva evaluación</span>
          <button className="modal-x" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="field">
              <label>Nombre completo</label>
              <input
                type="text"
                placeholder="Ej. Juan Pérez García"
                value={form.nombre_completo}
                onChange={(e) => setForm(f => ({ ...f, nombre_completo: e.target.value }))}
                required
                minLength={2}
                maxLength={200}
              />
            </div>
            <div className="field">
              <label>Correo electrónico</label>
              <input
                type="email"
                placeholder="juan@ejemplo.com"
                value={form.email}
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                required
              />
            </div>
            <div className="field">
              <label>Puesto aplicado</label>
              <input
                type="text"
                placeholder="Ej. Ejecutivo de ventas"
                value={form.puesto_aplicado}
                onChange={(e) => setForm(f => ({ ...f, puesto_aplicado: e.target.value }))}
                required
                maxLength={150}
              />
            </div>
            <div className="field">
              <label>Vigencia (horas)</label>
              <input
                type="number"
                min={1}
                max={720}
                value={form.vigencia_horas}
                onChange={(e) => setForm(f => ({ ...f, vigencia_horas: Number(e.target.value) }))}
                required
              />
            </div>
            {error && <div className="field-error">{error}</div>}
          </div>
          <div className="modal-ftr">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={mutation.isPending}>
              {mutation.isPending ? 'Creando…' : 'Crear evaluación'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal: Credenciales (una sola vez) ────────────────────────────────────────

interface CredencialesModalProps {
  nombre: string;
  token: string;
  password: string;
  onClose: () => void;
}

function CredencialesModal({ nombre, token, password, onClose }: CredencialesModalProps) {
  const link = `${EVAL_BASE}/${token}`;
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedPass, setCopiedPass] = useState(false);

  function copiar(text: string, setCopied: (v: boolean) => void) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="backdrop">
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-hdr">
          <span className="modal-title">Credenciales de acceso — {nombre}</span>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div
            style={{
              background: '#fef9c3',
              border: '1px solid #fde047',
              borderRadius: 8,
              padding: '10px 14px',
              fontSize: 12.5,
              color: '#713f12',
              lineHeight: 1.5,
            }}
          >
            <strong>Atención:</strong> esta es la única vez que se muestra la contraseña. Cierra
            este modal solo después de haberla comunicado al candidato. No es posible recuperarla.
          </div>

          <div className="field">
            <label>Liga de evaluación</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                readOnly
                value={link}
                style={{ flex: 1, background: '#f8fafc', cursor: 'text' }}
              />
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => copiar(link, setCopiedLink)}
                style={{ whiteSpace: 'nowrap' }}
              >
                {copiedLink ? '✓ Copiada' : 'Copiar'}
              </button>
            </div>
          </div>

          <div className="field">
            <label>Contraseña temporal</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                readOnly
                value={password}
                style={{ flex: 1, background: '#f8fafc', fontFamily: 'monospace', letterSpacing: 2, cursor: 'text' }}
              />
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => copiar(password, setCopiedPass)}
                style={{ whiteSpace: 'nowrap' }}
              >
                {copiedPass ? '✓ Copiada' : 'Copiar'}
              </button>
            </div>
          </div>
        </div>
        <div className="modal-ftr">
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Entendido, ya la comuniqué
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────

export function AspirantesPage() {
  const { get } = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showNueva, setShowNueva] = useState(false);
  const [credenciales, setCredenciales] = useState<{
    nombre: string;
    token: string;
    password: string;
  } | null>(null);

  const { data: aspirantes, isLoading } = useQuery({
    queryKey: ['aspirantes'],
    queryFn: () => get<AspiranteRow[]>('/psicometricos/aspirantes'),
  });

  function handleCreado(res: CrearAspiranteResponse) {
    setShowNueva(false);
    queryClient.invalidateQueries({ queryKey: ['aspirantes'] });
    setCredenciales({
      nombre: res.aspirante.nombre_completo,
      token: res.invitacion.token_liga,
      password: res.password_plano,
    });
  }

  function handleCerrarCredenciales() {
    setCredenciales(null);
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--heading)', marginBottom: 2 }}>Aspirantes</h1>
          <p style={{ fontSize: 12.5, color: 'var(--muted)' }}>
            Panel de evaluaciones psicométricas
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNueva(true)}>
          + Nueva evaluación
        </button>
      </div>

      <div className="table-wrap">
        <div className="table-scroll">
          <table className="main-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Puesto</th>
                <th>Invitación</th>
                <th>Generada</th>
                <th>Expira</th>
                <th>Cleaver</th>
                <th>Kostick</th>
                <th>Terman</th>
                <th>LIFO</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td className="empty-td" colSpan={9}>Cargando…</td>
                </tr>
              )}
              {!isLoading && (!aspirantes || aspirantes.length === 0) && (
                <tr>
                  <td className="empty-td" colSpan={9}>Sin aspirantes registrados. Crea la primera evaluación.</td>
                </tr>
              )}
              {aspirantes?.map((asp) => {
                return (
                  <tr
                    key={asp.id}
                    className="clickable"
                    onClick={() => navigate(`/aspirantes/${asp.id}`)}
                  >
                    <td style={{ fontWeight: 600 }}>{asp.nombre_completo}</td>
                    <td>{asp.puesto_aplicado}</td>
                    <td>
                      {asp.invitacion
                        ? invBadge(asp.invitacion.estado as Parameters<typeof invBadge>[0])
                        : <span className="badge badge-neutral">Sin inv.</span>
                      }
                    </td>
                    <td style={{ color: 'var(--muted)' }}>
                      {asp.invitacion ? fmtFecha(asp.invitacion.generada_en) : '—'}
                    </td>
                    <td style={{ color: 'var(--muted)' }}>
                      {asp.invitacion ? fmtFecha(asp.invitacion.expira_en) : '—'}
                    </td>
                    {EXAMENES_COLUMNAS.map((tipo) => {
                      const aplic = asp.aplicaciones.find(a => a.tipo_examen === tipo);
                      return (
                        <td key={tipo}>
                          {aplic
                            ? aplicacionBadge(aplic.estado)
                            : <span style={{ color: 'var(--muted)', fontSize: 12 }}>—</span>
                          }
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {aspirantes && (
          <div className="table-footer">
            <span className="table-count">{aspirantes.length} aspirante{aspirantes.length !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {showNueva && (
        <NuevaEvaluacionModal
          onClose={() => setShowNueva(false)}
          onSuccess={handleCreado}
        />
      )}

      {credenciales && (
        <CredencialesModal
          nombre={credenciales.nombre}
          token={credenciales.token}
          password={credenciales.password}
          onClose={handleCerrarCredenciales}
        />
      )}
    </>
  );
}
